// Expiry tracking fields (F-CTRL-N73), shared by every form that creates an
// item or brings stock in, so "tracked means a date is needed" reads and
// behaves the same everywhere.
import { DatePicker } from '@/components/ui/date-picker'
import { cn } from '@/lib/utils'
import { type ExpiryTracked, expiryFieldLabel, expiryMissing, parseShelfLife } from '@/lib/expiry'
import { FormField } from '@/components/store/inventory-form-ui'

/** The item-level setting: is this item perishable, and how long does it last. */
export function ExpiryTrackingFields({
  tracks,
  shelfLife,
  onTracksChange,
  onShelfLifeChange,
}: {
  tracks: boolean
  /** Raw field text; '' = not set. */
  shelfLife: string
  onTracksChange: (v: boolean) => void
  onShelfLifeChange: (v: string) => void
}) {
  const parsed = parseShelfLife(shelfLife)
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" className="mt-0.5 h-4 w-4" checked={tracks} onChange={(e) => onTracksChange(e.target.checked)} />
        <span>
          <span className="text-sm font-medium">Track expiry</span>
          <span className="block text-xs text-muted-foreground">
            For stock that goes off (milk, meat, sauces). Every delivery of this item will then need an expiry date.
          </span>
        </span>
      </label>
      {tracks && (
        <div>
          <FormField label="Shelf life (days)">
            <input
              type="number"
              inputMode="numeric"
              min="1"
              max="3650"
              step="1"
              placeholder="e.g. 5"
              value={shelfLife}
              onChange={(e) => onShelfLifeChange(e.target.value)}
            />
          </FormField>
          <p className={cn('mt-1 text-xs', parsed.error ? 'text-danger-fg' : 'text-muted-foreground')}>
            {parsed.error ?? 'Optional. How long a fresh delivery normally lasts. It fills in the expiry date for you.'}
          </p>
        </div>
      )}
    </div>
  )
}

/** The expiry date of stock being received: required for a tracked item. */
export function ExpiryDateField({
  id,
  item,
  value,
  onChange,
  prefilled,
  min,
  className,
  labelClassName,
}: {
  id: string
  item: ExpiryTracked | null | undefined
  value: string
  onChange: (v: string) => void
  /** True while the value is the shelf-life default nobody has touched. */
  prefilled?: boolean
  min?: string
  /** Classes for the picker trigger, to match the form it sits in. */
  className?: string
  labelClassName?: string
}) {
  const missing = expiryMissing(item, value)
  return (
    <div className="space-y-1">
      <label htmlFor={id} className={cn('block text-sm font-medium', labelClassName)}>
        {expiryFieldLabel(item)}
        {item?.tracks_expiry && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <DatePicker id={id} className={cn('w-full', className)} value={value} onChange={onChange} min={min} placeholder="Pick the date on the pack" />
      {missing ? (
        <p className="text-xs text-danger-fg">This item is tracked for expiry, so the date is needed.</p>
      ) : prefilled && value ? (
        <p className="text-xs text-muted-foreground">
          Filled in from the {item?.shelf_life_days}-day shelf life. Change it if the pack says otherwise.
        </p>
      ) : null}
    </div>
  )
}
